// @ts-strict-ignore

import { $iq } from "strophe.js";
import { allSettled } from "../../utils/allSettled";
import { SDP, SDPUtil } from "./strophe.jingle.sdp";
import { stringifyError } from "../../utils/stringifyError";

export enum MediaStreamKind {
    /**
     * Audio only
     */
    AUDIO = "audio",

    /**
     * Video only
     */
    VIDEO = "video",

    /**
     * Audio and video bundle
     */
    AUDIO_VIDEO_BUNDLE = "audiovideo",

    /**
     * Screen Sharing only
     */
    SHARING = "sharing",

    /**
     * Screen Sharing and audio of system
     */
    SHARING_BUNDLE = "sharingbundle"
}

export class JingleSession {

    //connected user JID
    public me: string;

    //unique ID of the session
    public sid: string;

    //XMPP Strophe Connection object reference
    public connection: any;

    //the initiator JID
    public initiator: string;

    //the responder JID
    public responder: string;

    //TRUE if the current user is the initiator of the jingle session
    public isInitiator: boolean = false;

    //the JID or the FULL JID of the distant party
    public peerjid: string;

    //the current state (useful ?)
    public state: string;

    //the RTC Peer connection object
    public peerconnection: RTCPeerConnection;

    //array of the local streams available in the session
    public localStreams: MediaStream[] = [];

    //all incoming remote streams, with key value "mid"
    public remoteStreamsObject: Record<string, MediaStream> = {};
    public remoteStream: any; // Dobro WTF??? (cf: webrtcP2PRecording service line ~400)
    public remoteStreams: any[]; // Dobro WTF???

    //data channels infos
    public application: any = {};

    //is the current session in progress of renegotiation
    public isRenegotiating: boolean = false;

    // conference related data
    public conference_id: any = -1;
    //the conference ID in case of SFU conf / Webinar
    public confId: string;
    //the remote publisher ID, in case of SFU / Webinar
    public publisherId: string;

    //the type of the media (as string) for the session; Used for many things
    public remoteType: any = null;
    public localType: any = null;

    //the local & remote SDP
    public localSDP: any = null;
    public remoteSDP: any = null;

    //list of the transceivers in the session
    public localTransceivers: RTCRtpTransceiver[] = [];

    //is the session on hold
    public onHold: boolean = false;

    //logger instance
    public logger: any = null;

    //is the session related to ECHO TEST
    public isEchoTest: boolean = false;

    //the remote called number ?
    public calledNumber: any = null;

    //this parameter is used when we make a webrtc to webrt transfer call, for more info see RQRAINB-6616
    public replaceSid = "";

    //this parameter is used when we shift the call from one ressource to another
    public callShift = false;

    //If the session is related to a webinar
    public isWebinarSession: boolean = false;

    //used to manage reconnection timeout
    public autoReconnectTimer;

    //the reason why the session is terminated;
    //not sure if used
    private reason: string;

    /**
     * SESSION SETTINGS
     */

    //specific configuration when making offer or answer, usually used in WEBRTC GW calls
    public media_constraints: RTCOfferOptions | RTCAnswerOptions;

    //the configuration used for the Peer connection
    public ice_config: any = {};

    //company configuration for the WEBRTC
    public company_config: any = {};

    //default bandwidth settings for P2P & conf
    private simulcastSettings = {
        high: 1300000,
        medium: 400000,
        low: 150000
    };

    private bandwidthLimitSettings = {
        video: 1200,
        sharing: 1500
    };

    // Discontinuous Transmission (DTX)
    private DTX: boolean = false;

    //list of types of candidates to be filterred; Settings set for the company via the company_config
    private iceFilterList: any[] = [];

    //QoS DSCP values added to the audio/video packets
    public enableDSCP: boolean = false;

    // IQ timeout before errors is send
    private jingleIqTimeout = 15000;

    //will be used as an ID set in the SDP
    public timestamp: number;
    public remoteSDPNumber: number = 2;

    /**
     * WEBRTC STATS INFOS
     */
    public statsinterval: any = null;
    public webrtcStatsInterval: any;

    //the object containing all the stats data related to this session
    public webrtcStats = {
        iteration: 0,
        intervalDuration: 3000,
        audio: {
            totalAudioPacketsSent: 0,
            totalAudioPacketsReceived: 0,
            totalAudioPacketsLost: 0,
            bytesReceived: 0,
            bytesSent: 0,
            globalMOS: 0,
            meanMOS: 0,
            maxRTT: 0,
            globalJitter: 0,
            maxJitter: 0,
            globalRTT: 0,
            lastDeltaAvgRTT: 0,
            lastRoundTripTimeMeasurements: 0,
            lastAudioOutputLevel: -1,
            rms: 0,
            oldTotalSamplesDurationMediaPlayout: 0,
            oldTotalSynthesizedSamplesDuration: 0,
            synthesizedSamplesPlayed: 0
        },
        ice: {
            networkType: "",
            local: "",
            remote: ""
        },
        video: {
            totalVideoPacketsReceived: 0,
            totalVideoPacketsLost: 0,
            videoPacketsReceivedDelta: -1,
            videoPacketsReceivedDeltaEmptyCount: 0,
            videoPacketLostErrorCount: 0,
            videoPacketLostOKCount: 0,
            bytesReceived: 0
        },
        sharing: {
            bytesReceived: 0
        },
        ssrc: { }
    };

    /**
     * RECONNECTION STUFF
     */
    public isReconnecting: boolean = false;
    private savedIncomingTransportReplace: any = null;
    private createdOfferTimeout: any = null;

    //options that we can set and will be used when we reconnect the session
    public reconnectionOptions: any = null;

    //for some reason, some of the ICE candidates are sent too quickly, and the server is not ready to accept them;
    //so we put some small timeout before sending them
    private sendIceCandidatesPromise: any = null;

    public hasSentTransportReplace: boolean = false;

    /**
     * LYFE CYCLE AND APIs
     */
    public constructor(me: string, sid: string, connection: any, jingleIqTimeout: number = 15000) {
        this.me = me;
        this.sid = sid;
        this.connection = connection;
        this.jingleIqTimeout = jingleIqTimeout;
    }

    public async initiate(peerjid, isInitiator) {
        this.logger.log(`[Strophe.jingle] initiate ${this.sid}`);
        if (this.state) {
            this.logger.log('[Strophe.jingle] attempt to initiate on session ' + this.sid + 'in state ' + this.state);
            return;
        }
        this.isInitiator = isInitiator;
        this.initiator = isInitiator ? this.me : peerjid;
        this.responder = !isInitiator ? this.me : peerjid;
        this.peerjid = peerjid;

        const constraints: any = {
            optional: [
                { googScreencastMinBitrate: 100 },
                { googCpuOveruseDetection: true }
            ]
        };

        this.ice_config.sdpSemantics = "unified-plan";

        if (this.ice_config.dtx) {
            this.logger.log("[Strophe.jingle] Discontinuous Transmission (DTX) activated");
            this.DTX = true;
        }

        if (this.company_config) {
            //ice filter
            if (this.company_config.negotiation && this.company_config.negotiation.iceFiltering) {
                this.iceFilterList = (this.company_config.negotiation.iceFiltering.length) ? this.company_config.negotiation.iceFiltering : [];
            }

            //simulcast settings
            if (this.company_config.videoExt) {
                if (this.company_config.videoExt.simulcastProfileBandwidth) {
                    this.simulcastSettings.low = this.company_config.videoExt.simulcastProfileBandwidth.low ? this.company_config.videoExt.simulcastProfileBandwidth.low : this.simulcastSettings.low;
                    this.simulcastSettings.medium = this.company_config.videoExt.simulcastProfileBandwidth.medium ? this.company_config.videoExt.simulcastProfileBandwidth.medium : this.simulcastSettings.medium;
                    this.simulcastSettings.high = this.company_config.videoExt.simulcastProfileBandwidth.high ? this.company_config.videoExt.simulcastProfileBandwidth.high : this.simulcastSettings.high;
                }
                this.bandwidthLimitSettings.video = this.company_config.videoExt.maxBandwidth ? this.company_config.videoExt.maxBandwidth : this.bandwidthLimitSettings.video;
            }

            //sharing bandwidth limitations
            if (this.company_config.sharingExt) {
                this.bandwidthLimitSettings.sharing = this.company_config.sharingExt.maxBandwidth ? this.company_config.sharingExt.maxBandwidth : this.bandwidthLimitSettings.sharing;
            }

            if (this.company_config.qos?.enableDSCP) {
                this.logger.log("[Strophe.jingle] QoS DSCP is activated");
                this.enableDSCP = true;
            }

            //get the current company policy
            if (this.company_config.ice?.iceTransportPolicy) {
                if (this.company_config.ice?.iceTransportPolicy === 'all' || this.company_config.ice?.iceTransportPolicy === 'relay') { this.ice_config.iceTransportPolicy = this.company_config.ice.iceTransportPolicy; }
            }
        }

        try {
            this.peerconnection = new (window as any).RTCPeerConnection(this.ice_config, constraints);
        } catch (err) {
            this.logger.warn(`[Strophe.jingle] Failed to create PeerConnection for ${this.sid}, exception ${err?.message}`);
            this.logger.warn(err);

            try {
                this.peerconnection = new window.RTCPeerConnection();
                if (this.peerconnection.setConfiguration) {
                    this.peerconnection.setConfiguration(this.ice_config);
                }
            } catch (error) {
                this.logger.warn(`[Strophe.jingle] Failed to create PeerConnection for ${this.sid}, exception ${error?.message}`);
                this.logger.warn(error);
                return;
            }
        }

        this.addPeerConnectionListeners();

        //create m-line audio even when there's no audio to be sent;
        if (this.isInitiator) {
            if (!this.localStreams.length && this.conference_id !== -1 && this.conference_id && this.localType !== 'application') {
                if (!this.localTransceivers[0]) {
                    this.localTransceivers[0] = this.peerconnection.addTransceiver("audio", { direction: "recvonly" });
                }
            }
        }

        if (this.isInitiator && this.localType !== 'application') await this.updateSessionStreamsAndTransceivers(this.localStreams);

        //add starting delay for sending ICE candidates, as server is not ready to receive them
        this.sendIceCandidatesPromise = new Promise<void>((resolve) => {
            setTimeout(() => {
                resolve();
                this.sendIceCandidatesPromise = null;
            }, 500);
        });
    }

    private addPeerConnectionListeners() {
        this.peerconnection.onicecandidate = (event) => {
            if (this) {
                if (this.sendIceCandidatesPromise) {
                    this.logger.log(`[Strophe.jingle] onicecandidate on standby for session ${this.sid}`);
                    this.sendIceCandidatesPromise.then(() => {
                        this.sendIceCandidate(event.candidate);
                    });
                }
                else {
                    this.sendIceCandidate(event.candidate);
                }
            } else {
                this.logger.log(`[Strophe.jingle] onicecandidate not added due to renegotiation for ${this.sid}`);
            }
        };

        this.peerconnection.onicecandidateerror = (event) => {
            if (!event) return;
            const info = `${event.address} ${event.port} ${event.errorCode} ${event.url} ${event.errorText}`;

            if (event.errorCode === 401) {
                this.logger.debug(`[Strophe.jingle] onicecandidateerror -- TURN/STUN error received : ${event.address} ${event.port} ${event.errorCode} ${event.url} ${event.errorText} -- credentials expired`);
                //we need to update the current ICE config as the credentials have expired
                this.connection.jingle.rxSubject.next({ name: 'jingleTurnCredentialExpire', data: { sid: this.sid } });

                this.connection.jingle.rxSubject.next({ name: 'onicecandidateerror', data: { sid: this.sid, type: "TURN/STUN", info } });
            }
            else if (event.errorCode >= 300 && event.errorCode <= 699) {
                this.logger.debug(`[Strophe.jingle] onicecandidateerror -- TURN/STUN error received : ${event.address} ${event.port} ${event.errorCode} ${event.url} ${event.errorText}`);
                this.connection.jingle.rxSubject.next({ name: 'onicecandidateerror', data: { sid: this.sid, type: "TURN/STUN", info } });
                // STUN errors are in the range 300-699. See RFC 5389, section 15.6
                // for a list of codes. TURN adds a few more error codes; see
                // RFC 5766, section 15 for details.
            } else if (event.errorCode >= 700 && event.errorCode <= 799) {
                this.logger.debug(`[Strophe.jingle] onicecandidateerror -- Server could not be reached error received : ${info}`);
                this.connection.jingle.rxSubject.next({ name: 'onicecandidateerror', data: { sid: this.sid, type: "SERVER_NOT_REACHABLE", info } });
                // Server could not be reached; a specific error number is
                // provided but these are not yet specified.
            }
        };

        this.peerconnection.ontrack = (trackEvent) => {
            if (this.ice_config.citrix || this.ice_config.horizon) {
                this.logger.log("[Strophe.jingle] citrix / horizon ontrack");

                if (this.conference_id && this.conference_id !== -1) {
                    this.logger.log(`[Strophe.jingle] citrix / horizon ontrack added for conference`);
                    this.remoteStreamsObject[0] = trackEvent.streams[0];
                }
                else {
                    let mid = parseInt(trackEvent.transceiver.mid, 10);
                    if (isNaN(mid)) {
                        mid = 0;
                    }
                    this.logger.log(`[Strophe.jingle] citrix / horizon ontrack for mid ${mid} -- and stream ${trackEvent?.streams[0]}`);

                    //for citrixm the streams objects is wrong, we should use the "track" event
                    if (this.ice_config.citrix) {
                        this.logger.log(trackEvent);
                        this.remoteStreamsObject[mid] = window["CitrixWebRTC"].createMediaStream([trackEvent.track]);
                    }
                    else {
                        this.logger.log(trackEvent);
                        this.remoteStreamsObject[mid] = trackEvent.streams[0];
                    }
                }

                if (this.conference_id && this.conference_id !== -1) {
                    this.connection.jingle.rxSubject.next({ name: 'jingleUpdateStream', data: { sid: this.sid, medias: { audio: true, video: false }, confId: this.conference_id } });
                }
                else { this.connection.jingle.rxSubject.next({ name: 'jingleP2PUpdateStream', data: { sid: this.sid } }); }
            }
            else {
                if (this.conference_id && this.conference_id !== -1) {
                    this.createRemoteStreamForConference(trackEvent.transceiver);
                }
                else {
                    this.createOrUpdateRemoteStreams(trackEvent.transceiver);
                }
            }
        };

        this.peerconnection.oniceconnectionstatechange = () => {
            if (!(this && this.peerconnection)) return;
            this.logger.debug(`[Strophe.jingle] oniceconnectionstatechange for session ${this.sid} -- ${this.peerconnection.iceConnectionState}`);
            this.connection.jingle.rxSubject.next({ name: 'jingleConnectionStateChange', data: { sid: this.sid, session: this } });
        };

        this.peerconnection.onicegatheringstatechange = () => {
            if (!(this && this.peerconnection)) return;
            this.logger.debug(`[Strophe.jingle] onicegatheringstatechange event for ${this.sid} -- ${this.peerconnection.iceGatheringState}`);
        };

        this.peerconnection.onnegotiationneeded = () => {
            if (!(this && this.peerconnection)) return;
            this.logger.debug(`[Strophe.jingle] onnegotiationneeded for session ${this.sid}`);
        };

        this.peerconnection.onsignalingstatechange = () => {
            if (!(this && this.peerconnection)) return;

            this.logger.debug(`[Strophe.jingle] onsignalingstatechange for session ${this.sid} -- ${this.peerconnection.signalingState}`);
            this.connection.jingle.rxSubject.next({ name: 'jingleConnectionStateChange', data: { sid: this.sid, session: this } });
        };

        this.peerconnection.onconnectionstatechange = () => {
            if (!(this && this.peerconnection)) return;

            //@ts-expect-error
            if (this.ice_config.citrix && this.peerconnection.connectionState_) {
                //@ts-expect-error
                this.peerconnection.connectionState = this.peerconnection.connectionState_;
            }

            this.logger.debug(`[Strophe.jingle] onconnectionstatechange for session ${this.sid} -- ${this.peerconnection.connectionState}`);
            this.connection.jingle.rxSubject.next({ name: 'jingleConnectionStateChange', data: { sid: this.sid, session: this } });
        };
    }

    private createRemoteStreamForConference(transceiver) {
        let stream = this.remoteStreamsObject[0];
        if (!stream) { stream = this.remoteStreamsObject[0] = new MediaStream(); }

        let existingTrack = null;
        if (transceiver.receiver.track.kind === 'audio') { existingTrack = stream.getAudioTracks()[0]; }
        if (transceiver.receiver.track.kind === 'video') { existingTrack = stream.getVideoTracks()[0]; }
        if (existingTrack) { stream.removeTrack(existingTrack); }
        stream.addTrack(transceiver.receiver.track);

        const audio = stream.getAudioTracks()[0] !== undefined;
        const video = stream.getVideoTracks()[0] !== undefined;
        const medias = { audio: audio, video: video };
        this.connection.jingle.rxSubject.next({ name: 'jingleUpdateStream', data: { sid: this.sid, medias: medias, confId: this.conference_id } });
    }

    private createOrUpdateRemoteStreams(transceiver) {
        let mid = parseInt(transceiver.mid, 10);
        if (isNaN(mid)) {
            mid = 0;
        }
        const stream = new MediaStream();
        if (this.remoteStreamsObject[mid]) {
            this.remoteStreamsObject[mid].removeTrack(this.remoteStreamsObject[mid].getTracks()[0]);
            this.remoteStreamsObject[mid].addTrack(transceiver.receiver.track);
        }
        else {
            stream.addTrack(transceiver.receiver.track);
            this.remoteStreamsObject[mid] = stream;
        }

        this.connection.jingle.rxSubject.next({ name: 'jingleP2PUpdateStream', data: { sid: this.sid } });
    }

    public updateRemoteStreams() {
        this.localTransceivers.forEach((transceiver) => {
            if (this.ice_config.horizon) {
                //horizon does not update correctly the current direction...
                //so we need to use a workaround ...
                if (this.remoteType === "audio") {
                    this.remoteStreamsObject["1"] = null;
                    this.remoteStreamsObject["2"] = null;
                }
                else if (this.remoteType === "sharing") {
                    this.remoteStreamsObject["1"] = null;
                }
                else if (this.remoteType === "video") {
                    this.remoteStreamsObject["2"] = null;
                }
            }
            else {
                this.logger.debug(`[Strophe.jingle] updateRemoteStreams transceiver.currentDirection ${transceiver?.currentDirection} transceiver.mid : ${transceiver?.mid}`);
                if (!transceiver.currentDirection || transceiver.currentDirection === "sendonly" || transceiver.currentDirection === "inactive") {
                    this.remoteStreamsObject[transceiver.mid] = null;
                }
            }
        });
    }

    public replaceVideoTrack(videoTrack) {
        if (this.localTransceivers[1]) {
            this.localTransceivers[1].sender.replaceTrack(videoTrack);
            this.localTransceivers[1].direction = "sendonly";
        }
    }

    //New API that is used to change audio track "on the fly"; If the action is rejected
    // we should renegociate the call with SDP;
    public replaceTransceiverTrack(track, id = 0) {
        return new Promise<void>((resolve, reject) => {
            try {
                if (!id) { id = 0; }
                if (this.localTransceivers[id]) {
                    this.localTransceivers[id].sender.replaceTrack(track)
                        .then(() => {
                            resolve();
                        })
                        .catch(() => {
                            reject();
                        });
                }
            }
            catch {
                reject();
            }
        });
    }

    public async updateSessionStreamsAndTransceivers(streams: MediaStream[] = []) {
        if (this.conference_id && this.conference_id !== -1) {
            //manage conference session
            if (streams?.length) {
                await this.updateConferenceOrWebinarStreams(streams[0]);
            }
        }
        else {
            await this.updateP2PSessionWithNewStreams(streams);
        }
    }

    private async updateConferenceOrWebinarStreams(stream: MediaStream) {
        try {
            this.logger.log(`[Strophe.jingle.session] updateConferenceOrWebinarStreams for ${this.sid}`);

            //manage tracks
            let audioTrack; let videoTrack; let sharingTrack; let audioOfSharingTrack;

            //manage index of the transciver we want to modify
            let videoIndex; let audioIndex; let sharingIndex; let sharingAudioIndex;

            const promises = [];

            if (stream["mediaStreamKind"] === MediaStreamKind.SHARING_BUNDLE) {
                sharingIndex = 0;
                sharingAudioIndex = 1;
                sharingTrack = stream.getVideoTracks()[0];
                audioOfSharingTrack = stream.getAudioTracks()[0];
            }
            else if (stream["mediaStreamKind"] === MediaStreamKind.SHARING) {
                sharingIndex = 0;
                sharingAudioIndex = 1;
                sharingTrack = stream.getVideoTracks()[0];
            }
            else {
                audioIndex = 0;
                videoIndex = 0;
                audioTrack = stream.getAudioTracks()[0];
                videoTrack = stream.getVideoTracks()[0];
            }

            //create the transceivers, if needed
            if (audioTrack) {
                if (!this.localTransceivers[0]) {
                    this.localTransceivers[0] = this.peerconnection.addTransceiver("audio", { direction: "sendrecv", streams: undefined });
                }

                //for webinars, audio and video are in bundle mode, so video index is 1; For all other cases it's 0
                videoIndex = 1;
            }

            if (videoTrack && !this.localTransceivers[videoIndex]) {
                //manage simulcast or not based on Conf or Webinar
                this.localTransceivers[videoIndex] = this.addVideoTransceiverForConfOrWebinar();
            }

            if (sharingTrack && !this.localTransceivers[0]) { this.localTransceivers[0] = this.peerconnection.addTransceiver("video", { direction: "sendonly", streams: undefined }); }

            if (audioOfSharingTrack && !this.localTransceivers[1]) { this.localTransceivers[1] = this.peerconnection.addTransceiver("audio", { direction: "sendrecv", streams: undefined }); }

            //update tracks based on the streams we've

            //audio track
            if (audioIndex === 0 && audioTrack) { promises.push(this.replaceTrackInTransceiver(this.localTransceivers[audioIndex], audioTrack)); }

            //video track
            if (videoIndex === 0 || videoIndex === 1) {
                if (this.localTransceivers[videoIndex]) { promises.push(this.replaceTrackInTransceiver(this.localTransceivers[videoIndex], videoTrack)); }
            }

            //sharing track
            if (sharingIndex === 0) { promises.push(this.replaceTrackInTransceiver(this.localTransceivers[0], sharingTrack)); }

            //sharing and audio track
            if (sharingAudioIndex === 1 && this.localTransceivers[1]) { promises.push(this.replaceTrackInTransceiver(this.localTransceivers[1], audioOfSharingTrack)); }

            await allSettled(promises);
        }
        catch (error) {
            this.logger.error(`[Strophe.jingle.session] updateConferenceOrWebinarStreams for ${this.sid} error -- ${error?.message}`);
        }
    }

    private addVideoTransceiverForConfOrWebinar(): RTCRtpTransceiver {
        //for webinars or if simulcast is disabled, bring back normal transceiver
        if (this.localType === "audio+video" || !this.ice_config.simulcast) { return this.peerconnection.addTransceiver("video", { direction: "sendonly", streams: undefined }); }

        //try to build simulcast transceiver
        try {
            //check for test flags for codecs
            if (localStorage.getItem('webinarVideoCodec') === 'vp9' || localStorage.getItem('webinarVideoCodec') === 'av1') {

                const scalabilityMode = localStorage.getItem('scalabilityMode') ? localStorage.getItem('scalabilityMode') : 'L2T3';
                this.logger.log(`[Strophe.jingle] Enabling ${localStorage.getItem('webinarVideoCodec')} and ${scalabilityMode} for ${this.sid}`);
                return this.peerconnection.addTransceiver("video", {
                    sendEncodings: [
                    //@ts-ignore
                        { scalabilityMode: scalabilityMode, active: true, maxBitrate: this.simulcastSettings.high }
                    ]
                });
            }

            const scalabilityMode = localStorage.getItem('scalabilityMode') ? localStorage.getItem('scalabilityMode') : 'L1T2';
            this.logger.log(`[Strophe.jingle] Enabling Simulcasting for ${this.sid} with settings: low ${this.simulcastSettings.low}, medium: ${this.simulcastSettings.medium}, high: ${this.simulcastSettings.high}, scalability: ${scalabilityMode}`);

            return this.peerconnection.addTransceiver("video", {
                sendEncodings: [
                    //@ts-ignore
                    { rid: "l", active: true, maxBitrate: this.simulcastSettings.low, scaleResolutionDownBy: 4, scalabilityMode: scalabilityMode },
                    //@ts-ignore
                    { rid: "m", active: true, maxBitrate: this.simulcastSettings.medium, scaleResolutionDownBy: 2, scalabilityMode: scalabilityMode },
                    //@ts-ignore
                    { rid: "h", active: true, maxBitrate: this.simulcastSettings.high, scalabilityMode: scalabilityMode }
                ]
            });
        }
        catch (error) {
            this.logger.warn(`[Strophe.jingle] Enabling simulcast for ${this.sid} error -- ${error?.message}; Return basic video without simulcast`);
            return this.peerconnection.addTransceiver("video", { direction: "sendonly", streams: undefined });
        }
    }

    private async updateP2PSessionWithNewStreams(streams: MediaStream[] = []) {
        //we need to synchronise the current streams and the trasceivers in the session

        //update local transceivers
        this.localTransceivers = this.peerconnection.getTransceivers();

        //find audio track
        //find video track
        //find sharing track
        //find audio on top of sharing track
        let audioTrack;
        let videoTrack;
        let sharingTrack;
        let audioOfSharingTrack;

        this.localStreams = streams;

        streams.forEach((stream) => {
            if (stream["mediaStreamKind"] === MediaStreamKind.SHARING_BUNDLE) {
                sharingTrack = stream.getVideoTracks()[0];
                audioOfSharingTrack = stream.getAudioTracks()[0];
            }
            else if (stream["mediaStreamKind"] === MediaStreamKind.SHARING) {
                sharingTrack = stream.getVideoTracks()[0];
            }
            else {
                audioTrack = stream.getAudioTracks()[0];
                videoTrack = stream.getVideoTracks()[0];
            }
        });

        //create or update existing transceivers
        const promises = [];

        //we always have at least one transceiver
        if (!this.localTransceivers[0]) {
            this.localTransceivers[0] = this.peerconnection.addTransceiver("audio", { direction: "sendrecv", streams: undefined });
        }

        //if video track, add second transceiver if not there
        if (videoTrack) {
            if (!this.localTransceivers[1]) this.localTransceivers[1] = this.peerconnection.addTransceiver("video", { direction: "sendrecv", streams: undefined });
        }

        if (sharingTrack) {
            if (!this.localTransceivers[1]) this.localTransceivers[1] = this.peerconnection.addTransceiver("video", { direction: "sendrecv", streams: undefined });
            if (!this.localTransceivers[2]) this.localTransceivers[2] = this.peerconnection.addTransceiver("video", { direction: "sendrecv", streams: undefined });
        }

        if (audioOfSharingTrack) {
            if (!this.localTransceivers[3]) this.localTransceivers[3] = this.peerconnection.addTransceiver("audio", { direction: "sendrecv", streams: undefined });
        }

        //update the tracks in transceivers
        promises.push(this.replaceTrackInTransceiver(this.localTransceivers[0], audioTrack));

        if (this.localTransceivers[1]) { promises.push(this.replaceTrackInTransceiver(this.localTransceivers[1], videoTrack)); }

        //update sharing track
        if (this.localTransceivers[2]) { promises.push(this.replaceTrackInTransceiver(this.localTransceivers[2], sharingTrack)); }

        //audio + sharing track
        if (this.localTransceivers[3]) { promises.push(this.replaceTrackInTransceiver(this.localTransceivers[3], audioOfSharingTrack)); }

        await allSettled(promises);

        //set codec preferences based on the configuration
        //only for direct P2P calls and no conferences
        this.setCodecPreferencesForSession();

        console.error(this.localTransceivers);
    }

    private async replaceTrackInTransceiver(transceiver: RTCRtpTransceiver, track?: MediaStreamTrack) {
        try {
            await transceiver.sender.replaceTrack(track);
            if (this.conference_id && this.conference_id !== -1) {
                //for conferences and webinars, video and sharing is either sendonly or inactive
                if (this.localType === 'audio+video' || this.localType === "video" || this.localType === "sharing") { transceiver.direction = track ? 'sendonly' : 'inactive'; }
                else transceiver.direction = track ? 'sendrecv' : 'recvonly';
            }
            else { transceiver.direction = track ? 'sendrecv' : 'recvonly'; }
        }
        catch (error) {
            this.logger.error(`[JingleSession] replaceTrackInTransceiver for session ${this.sid} and mid ${transceiver.mid} -- error ${stringifyError(error)}`);
        }
    }

    public terminate(reason?: any) {
        try {
            this.state = 'ended';
            this.reason = reason;
            if (this.peerconnection && this.peerconnection.signalingState !== "closed") {
                this.peerconnection.close();
            }

            if (this.peerconnection) {
                this.peerconnection.onicecandidate = null;
                this.peerconnection.onconnectionstatechange = null;
                this.peerconnection.onicecandidateerror = null;
                this.peerconnection.onsignalingstatechange = null;
                this.peerconnection.onicegatheringstatechange = null;
                this.peerconnection.onnegotiationneeded = null;
                this.peerconnection.oniceconnectionstatechange = null;
                //@ts-ignore
                this.peerconnection.onremovetrack = null; this.peerconnection.onaddstream = null; this.peerconnection.onaddtrack = null; this.peerconnection.onremovestream = null;
                this.peerconnection.ontrack = null;
            }
            this.logger.log(`[Strophe.jingle] Clear peerconnection ${this.sid}`);

            this.peerconnection = null;

            if (this.statsinterval !== null) {
                window.clearInterval(this.statsinterval);
                this.statsinterval = null;
            }

            if (this.webrtcStatsInterval) {
                window.clearInterval(this.webrtcStatsInterval);
                this.webrtcStatsInterval = null;
            }

            if (this.localStreams && this.localStreams.length) {
                while (this.localStreams.length) {
                    let stream = this.localStreams.pop();
                    stream.getTracks().forEach(function (track) {
                        track.enabled = false;
                        track.stop();
                        track = null;
                    });

                    stream = null;
                }
            }

            if (this.ice_config.horizon) {
                this.remoteStreamsObject = {};
                this.localTransceivers = [];
                return;
            }

            if (this.localTransceivers && this.localTransceivers.length) {
                while (this.localTransceivers.length) {
                    let transceiver = this.localTransceivers.pop();
                    if (transceiver && transceiver.sender) {
                        if (transceiver.sender.track && transceiver.sender.track.stop) {
                            transceiver.sender.track.stop();
                        }
                    }
                    if (transceiver && transceiver.receiver) {
                        if (transceiver.receiver.track && transceiver.receiver.track.stop) {
                            transceiver.receiver.track.stop();
                        }
                    }

                    transceiver = null;
                }
            }

            Object.keys(this.remoteStreamsObject).forEach((key) => {
                if (this.remoteStreamsObject[key]) {
                    this.remoteStreamsObject[key].getTracks().forEach(function (track) {
                        track.enabled = false;
                        track.stop();
                        track = null;
                    });

                    this.remoteStreamsObject[key] = null;
                }
            });
            this.remoteStreamsObject = {};
            this.localTransceivers = [];
        }
        catch (error) {
            this.logger.warn(`[Strophe.jingle] Clear peerconnection error ${error}`);
        }
    }

    public active() {
        return this.state === 'active';
    }

    private sendIceCandidate(candidate) {
        if (candidate && this.iceFilterList && this.iceFilterList.length) {
            if (this.iceFilterList.indexOf(candidate.type) !== -1) {
                this.logger.log('[Strophe.jingle] ICE candidate ' + candidate.type + ' is filtered');
                return;
            }
        }

        if (candidate) {
            const ice = SDPUtil.iceparams(this.localSDP.media[candidate.sdpMLineIndex], this.localSDP.session);
            const jcand = SDPUtil.candidateToJingle(candidate.candidate, this.logger);
            if (!(ice && jcand)) {
                this.logger.log('[Strophe.jingle] failed to get ice && jcand');
                return;
            }
            ice.xmlns = 'urn:xmpp:jingle:transports:ice-udp:1';

            this.sendIceCandidates([candidate]);
        }
    }

    private sendIceCandidates(candidates) {
        try {
            const cand = $iq({ to: this.peerjid, type: 'set' })
                .c('jingle', {
                    xmlns: 'urn:xmpp:jingle:1',
                    action: 'transport-info',
                    initiator: this.initiator,
                    sid: this.sid,
                    localType: this.localType
                });
            for (let mid = 0; mid < this.localSDP.media.length; mid++) {
                const cands = candidates.filter(function (el) { return el.sdpMLineIndex === mid; });
                const mline = SDPUtil.parse_mline(this.localSDP.media[mid].split('\r\n')[0]);
                if (cands.length > 0) {
                    const ice = SDPUtil.iceparams(this.localSDP.media[mid], this.localSDP.session);
                    ice.xmlns = 'urn:xmpp:jingle:transports:ice-udp:1';
                    cand.c('content', {
                        creator: this.initiator === this.me ? 'initiator' : 'responder',
                        name: (cands[0].sdpMid ? cands[0].sdpMid : mline.media)
                    }).c('transport', ice);
                    for (let i = 0; i < cands.length; i++) {
                        cand.c('candidate', SDPUtil.candidateToJingle(cands[i].candidate, null)).up();
                    }
                    // add fingerprint
                    if (SDPUtil.find_line(this.localSDP.media[mid], 'a=fingerprint:', this.localSDP.session)) {
                        const tmp = SDPUtil.parse_fingerprint(SDPUtil.find_line(this.localSDP.media[mid], 'a=fingerprint:', this.localSDP.session));
                        tmp.required = true;
                        tmp.xmlns = 'urn:xmpp:jingle:apps:dtls:0';
                        cand.c('fingerprint').t(tmp.fingerprint);
                        delete tmp.fingerprint;
                        cand.attrs(tmp);
                        cand.up();
                    }
                    cand.up(); // transport
                    cand.up(); // content
                }
            }

            this.connection.sendIQ(
                cand,
                () => { const ack: any = {}; ack.source = 'transportinfo'; },
                (stanza: any) => {
                    const error: any = ($(stanza).find('error').length)
                        ? {
                            code: $(stanza).find('error').attr('code'),
                            reason: $(stanza).find('error :first')[0].tagName,
                        }
                        : {};
                    error.source = 'transportinfo';
                    this.connection.jingle.rxSubject.next({ name: 'jingleError', data: { sid: this.sid, error: error } });
                },
                this.jingleIqTimeout
            );

        } catch (error) {
            this.logger.log('[Strophe.jingle] JingleSession.prototype.sendIceCandidates failed: ' + error);
        }
    }

    public hackOfferSDP(offer: RTCSessionDescriptionInit) {
        //Bug correction ICE Restart
        if ((window as any).adapter.browserDetails.browser !== "firefox") {
            offer.sdp = offer.sdp.replace(/a=sendonly/g, "a=sendrecv");
        }

        // Change direction to send only for conference video (Sender case)
        if (this.conference_id && this.conference_id !== -1 && (this.localType === "video" || this.localType === "sharing")) {
            offer.sdp = offer.sdp.replace(/a=sendrecv/g, "a=sendonly");
        }

        // Change direction to send only for webinar (Sender case)
        if (this.conference_id && this.conference_id !== -1 && this.localType === "audio+video") {
            offer.sdp = offer.sdp.replace(/a=sendrecv/g, "a=sendonly");
        }

        //Bug Firefox 92+ --> remove second SSRC and GROUP FID
        if ((!this.conference_id || this.conference_id === -1) && (window as any).adapter.browserDetails.browser === "firefox") {
            offer.sdp = this.removeSsrcGroupFidForFirefox(offer.sdp);
        }

        return offer;
    }

    // Created for specific issue as reported by CRRAINB-10995 Atrium#00455637 // Rainbow WebRTC calls not working for some users
    public hackMsLabelSDP(offer: RTCSessionDescriptionInit) {
        try {
            //Browser empty mslabel issue
            const res = offer.sdp.split('\r\n');
            for (let i = 0; i < res.length; i++) {
                if (res[i].substring(0, 7) === 'a=ssrc:') {
                    const idx = res[i].indexOf(' ');
                    if (res[i].substr(idx + 1).split(':', 2)[0]
                        && (res[i].substr(idx + 1).split(':', 2)[1] === undefined // Case for empty mslabel without :
                            || res[i].substr(idx + 1).split(':', 2)[1] === "")) { // Case for empty mslabel with :
                        res.splice(i, 1); // Remove erroneous line from SDP
                    }
                }
            }
            offer.sdp = res.join('\r\n');
            return offer;
        }
        catch {
            return offer;
        }
    }

    public async sendTransportReplace() {
        try {
            this.logger.log(`[Strophe.jingle] sendTransportReplace for session ${this.sid}`);

            const options: any = {};
            options.iceRestart = true;

            if (this.isReconnecting) {
                this.logger.log(`[Strophe.jingle] sendTransportReplace for session ${this.sid} -- ignored as already reconnecting`);
                return;
            }
            this.isReconnecting = true;

            const sdp = await this.peerconnection.createOffer(options);

            await this.createdOffer(sdp, "transport-replace");
        }
        catch (error) {
            this.logger.log(`[Strophe.jingle] sendTransportReplace for session ${this.sid} error -- ${error?.message}`);
        }
    }

    public async sendTransportReplaceWithAwait() {
        let options = {
            iceRestart: true
        };

        if (this.isReconnecting) {
            this.logger.log(`[Strophe.jingle] sendTransportReplace for ${this.sid} ignored because we're already connecting`);
            return;
        }

        this.logger.log(`[Strophe.jingle] sendTransportReplaceWithAwait for ${this.sid}`);

        this.isReconnecting = true;
        this.hasSentTransportReplace = true;

        if (this.reconnectionOptions) {
            options = this.reconnectionOptions;
        }

        try {
            const sdp = await this.peerconnection.createOffer(options);
            this.sendCreatedOfferWithAckWait(sdp, "transport-replace");
        }
        catch (error) {
            this.logger.log(`[Strophe.jingle] createOfferWithAckWait ${this.sid} failed ${error?.message}`);
        }
    }

    public async updateIceConfigAndReconnect(iceServers: [] = []) {
        try {
            if (this.peerconnection.setConfiguration) {
                this.ice_config.iceServers = iceServers;
                this.peerconnection.setConfiguration(this.ice_config);

                //see if the session needs to be reconnected
                if (this.peerconnection.connectionState === "disconnected" || this.peerconnection.connectionState === "failed") {
                    await this.reconnectSession();
                }
            }
        }
        catch (error) {
            this.logger.log(`[Strophe.jingle] updateIceConfigAndReconnect ${this.sid} failed -- ${error}`);
        }
    }

    public async reconnectSession() {
        try {
            let options: any = {};
            options.iceRestart = true;

            if (this.isReconnecting) {
                this.logger.log(`[Strophe.jingle] reconnectSession ${this.sid} ignored because we're already connecting`);
                return;
            }

            this.logger.log(`[Strophe.jingle] reconnectSession ${this.sid}`);

            this.isReconnecting = true;
            this.hasSentTransportReplace = true;

            if (this.reconnectionOptions) {
                options = this.reconnectionOptions;
            }

            //in case of MP call with RB Hub, the media modified message can be related to the "put on hold" / "unhold" events
            //we should store the current incoming SDP settings, so that we can generate correct SDP in case of transport-replace
            //so if we receive sendonly SDP for the m=audio media, then we should not resend audio in case of reconnection
            if (this.peerjid.indexOf("mp_rvcp") !== -1) {
                if (this.localTransceivers[0].currentDirection === "recvonly") {
                    this.localTransceivers[0].direction = "recvonly";
                }
            }

            const sdp = await this.peerconnection.createOffer(options);
            this.sendCreatedOfferWithAckWait(sdp, "transport-replace");
        }
        catch (error) {
            this.logger.log(`[Strophe.jingle] reconnectSession for session ${this.sid} failed -- ${error}`);
        }
    }

    public saveIncomingTransportReplace(elem, desctype, ack) {
        this.logger.log("[Strophe.jingle] saveIncomingTransportReplace");
        this.savedIncomingTransportReplace = {
            elem: elem,
            desctype: desctype,
            ack: ack
        };
    }

    public acceptSavedIncomingTransportReplace() {
        this.logger.log(`[Strophe.jingle] acceptSavedIncomingTransportReplace ${this.sid}`);
        this.connection.send(this.savedIncomingTransportReplace.ack);
        this.setRemoteDescription(this.savedIncomingTransportReplace.elem, this.savedIncomingTransportReplace.desctype);
        this.connection.jingle.rxSubject.next({ name: 'jingleTransportReplace', data: { sid: this.sid } });
        this.savedIncomingTransportReplace = null;
    }

    public rejectSavedIncomingTransportReplace() {
        this.logger.log(`[Strophe.jingle] rejectSavedIncomingTransportReplace ${this.sid}`);
        if (this.savedIncomingTransportReplace && this.savedIncomingTransportReplace.ack) {
            const ack = this.savedIncomingTransportReplace.ack;
            ack.type = 'error';
            ack.c('error', { type: 'reject' });
            this.connection.send(ack);
        }
        this.savedIncomingTransportReplace = null;
    }

    public sendCreatedOfferWithAckWait(offer: RTCSessionDescriptionInit, actionName) {
        try {
            offer = this.hackMsLabelSDP(offer);
            this.localSDP = new SDP(offer.sdp);
            const action = actionName ? actionName : 'session-initiate';

            this.logger.log(`[Strophe.jingle] sendCreatedOfferWithAckWait for ${this.sid} with action ${action} -- ${offer.sdp}`);

            let init = $iq({
                to: this.peerjid,
                type: 'set'
            })
                .c('jingle', {
                    xmlns: 'urn:xmpp:jingle:1',
                    action: action,
                    initiator: this.initiator,
                    sid: this.sid,
                    localType: this.localType
                });

            if (this.calledNumber && this.calledNumber !== "") { //callednumber for webrtcGateway-mediaPillar
                init = init.c('mediapillar', { xmlns: 'urn:xmpp:janus:1' })
                    .c('callednumber')
                    .t(this.calledNumber);
                init.up();
                init.up();
            }

            this.localSDP.toJingle(init, this.initiator === this.me ? 'initiator' : 'responder', this.conference_id, this.bandwidthLimitSettings);

            this.createdOfferTimeout = setTimeout(() => {
                this.logger.log(`[Strophe.jingle] sendCreatedOfferWithAckWait for ${this.sid} -- timeout expires -- reset all`);
                //transport-replace has been rejected
                this.hasSentTransportReplace = false;

                if (this.savedIncomingTransportReplace) {
                    this.acceptSavedIncomingTransportReplace();
                }
                else {
                    this.isReconnecting = false;
                }
            }, this.jingleIqTimeout + 1000);

            this.connection.sendIQ(init,
                async (stanza) => {
                    if (this.createdOfferTimeout) {
                        clearTimeout(this.createdOfferTimeout);
                        this.createdOfferTimeout = null;
                    }

                    if (stanza && $(stanza).find("error").length) {
                        //there's an error
                        //manage IQ reject
                        this.logger.log(`[Strophe.jingle] sendCreatedOfferWithAckWait ${this.sid} -- IQ ACK with error`);
                        //transport-replace has been rejected
                        this.hasSentTransportReplace = false;

                        if (this.savedIncomingTransportReplace) {
                            this.acceptSavedIncomingTransportReplace();
                        }

                        return;
                    }

                    this.hasSentTransportReplace = false;
                    if (this.savedIncomingTransportReplace) {
                        if (!this.isInitiator) {
                            //accept pending transport replace
                            this.acceptSavedIncomingTransportReplace();
                            return;
                        }
                        this.rejectSavedIncomingTransportReplace();
                    }

                    this.logger.log(`[Strophe.jingle] sendCreatedOfferWithAckWait ${this.sid} -- IQ ACK received, set local description`);

                    //if OK set local description
                    offer.sdp = this.localSDP.raw;
                    try {
                        await this.peerconnection.setLocalDescription(offer);
                        this.connection.jingle.rxSubject.next({ name: 'setLocalDescription.jingle', data: { sid: this.sid } });
                    }
                    catch (error) {
                        this.logger.error(`[Strophe.jingle] sendCreatedOfferWithAckWait -- setLocalDescription ${this.sid} error -- ${error?.message}`);
                    }
                },
                () => {
                    if (this.createdOfferTimeout) {
                        clearTimeout(this.createdOfferTimeout);
                        this.createdOfferTimeout = null;
                    }
                    //manage IQ reject
                    this.logger.log(`[Strophe.jingle] sendCreatedOfferWithAckWait ${this.sid} -- IQ rejected`);
                    //transport-replace has been rejected
                    this.hasSentTransportReplace = false;

                    if (this.savedIncomingTransportReplace) {
                        this.acceptSavedIncomingTransportReplace();
                    }
                    else {
                        this.isReconnecting = false;
                    }
                },
                this.jingleIqTimeout);
        } catch (error) {
            if (this.createdOfferTimeout) {
                clearTimeout(this.createdOfferTimeout);
                this.createdOfferTimeout = null;
            }
            this.logger.log(`[Strophe.jingle] sendCreatedOfferWithAckWait for ${this.sid} failed -- ${error?.message}`);
        }
    }

    public async sendOffer(iceRestart: boolean, application?: any): Promise<void> {
        try {
            this.logger.log(`Strophe.jingle] sendOffer (iceRestart : ${iceRestart})`);
            let options: any = {};

            if (!iceRestart) options = this.media_constraints;
            else {
                if ((this.conference_id === -1 && !this.calledNumber) || this.localType !== "video") options.iceRestart = true;
                if (this.conference_id !== -1) options.iceRestart = true;
            }

            if (this.ice_config.citrix) {
                options["mandatory"] = { offerToReceiveAudio: true };
            }

            const offer = await this.peerconnection.createOffer(options);
            this.createdOffer(offer, undefined, application);
        }
        catch (error) {
            this.logger.log(`[Strophe.jingle] sendOffer -- failure -- ${error.message}`);
        }
    }

    public async createdOffer(offer: RTCSessionDescriptionInit, actionName?: string, application?: any) {
        try {
            // Fix offer
            offer = this.hackOfferSDP(offer);
            offer = this.hackMsLabelSDP(offer);

            // Create localSDP
            this.localSDP = new SDP(offer.sdp);

            const action = actionName ? actionName : 'session-initiate';
            this.logger.log(`[Strophe.jingle] createdOffer(spd: ${offer.sdp}, action: ${action}`);
            this.connection.jingle.rxSubject.next({ name: 'createOffer.jingle', data: { sid: this.sid, action } });

            let init = $iq({
                to: this.peerjid,
                type: 'set'
            })
                .c('jingle', {
                    xmlns: 'urn:xmpp:jingle:1',
                    action: action,
                    initiator: this.initiator,
                    sid: this.sid,
                    localType: this.localType
                });

            if (application) {
                init = init.c("x-rainbow-app", { xmlns: 'urn:xmpp:jingle:rainbow:media:desc' })
                    .c('content', { id: application.id, name: application.name, description: application.description, affiliation: application.affiliation });
                init.up();
                init.up();
            }

            if (this.calledNumber && this.calledNumber !== "") { //callednumber for webrtcGateway-mediaPillar
                init = init.c('mediapillar', { xmlns: 'urn:xmpp:janus:1' })
                    .c('callednumber')
                    .t(this.calledNumber);
                init.up();
                init.up();
            }

            if (this.isEchoTest) {
                init = init.c('echotest', {
                    xmlns: 'urn:xmpp:janus:1'
                });
                init.up();
            }

            if (this.replaceSid && this.callShift) {
                init = init.c('replaces', {
                    sid: this.replaceSid,
                    callShift: 'true'
                });
                init.up();
            }
            else if (this.replaceSid) {
                init = init.c('replaces', {
                    sid: this.replaceSid
                });
                init.up();
            }

            this.localSDP.toJingle(init, this.initiator === this.me ? 'initiator' : 'responder', this.conference_id, this.bandwidthLimitSettings);

            //set the local description before sending it to the distant party
            offer.sdp = this.localSDP.raw;
            await this.peerconnection.setLocalDescription(offer);
            this.connection.jingle.rxSubject.next({ name: 'setLocalDescription.jingle', data: { sid: this.sid } });
            this.logger.log(`[Strophe.jingle] setLocalDescription success for ${this.sid}`);

            // this.addConferenceInfo(init);
            this.connection.sendIQ(init,
                () => {
                    const ack: any = {};
                    ack.source = 'offer';
                },
                (stanza) => {
                    this.logger.log(`[Strophe.jingle] IQ error: ${init}`);
                    this.state = 'error';
                    if (this.peerconnection && this.peerconnection.signalingState !== "closed") {
                        this.logger.log('[Strophe.jingle] Clear peerconnection');
                        this.peerconnection.close();
                        this.peerconnection = null;
                    }
                    const error: any = ($(stanza).find('error').length)
                        ? {
                            code: $(stanza).find('error').attr('code'),
                            reason: $(stanza).find('error :first')[0].tagName,
                        }
                        : {};
                    error.source = 'offer';
                    this.connection.jingle.rxSubject.next({ name: 'jingleError', data: { sid: this.sid, error: error } });
                },
                this.jingleIqTimeout);
        } catch (error) {
            this.logger.log('[Strophe.jingle] JingleSession.prototype.createdOffer failed: ' + error);
        }
    }

    public async contentAdd(mediaStreamType: string, addMediaLocalType: boolean = false): Promise<void> {
        try {
            this.logger.log(`[Strophe.jingle] contentAdd(mediaStreamType: ${mediaStreamType}, addMediaLocalType: ${addMediaLocalType})`);
            const sdp = await this.peerconnection.createOffer(this.media_constraints);
            this.contentUpdateOffer(sdp, mediaStreamType, 'add', addMediaLocalType);
        }
        catch (error) {
            this.logger.log('[Strophe.jingle] contentAdd failed ' + error.message);
        }
    }

    public async contentRemove(mediaStreamType: string, addMediaLocalType: boolean = false): Promise<void> {
        try {
            this.logger.log(`[Strophe.jingle] contentRemove(mediaStreamType: ${mediaStreamType}, addMediaLocalType: ${addMediaLocalType})`);
            console.dir(this.peerconnection);

            const sdp = await this.peerconnection.createOffer(this.media_constraints);
            this.contentUpdateOffer(sdp, mediaStreamType, 'remove', addMediaLocalType);
        }
        catch (error) {
            this.logger.log('[Strophe.jingle] contentRemove failed ' + error.message);
        }
    }

    public async contentModify(mediaStreamType: string, options?: any): Promise<void> {
        try {
            this.logger.log(`[Strophe.jingle] contentModify(mediaStreamType: ${mediaStreamType}`);

            if (!options) {
                options = {};
                if (this.reconnectionOptions) options.offerToReceiveAudio = this.reconnectionOptions.offerToReceiveAudio;
            }

            // In case of MP call with RB Hub, the media modified message can be related to the "put on hold" / "unhold" events
            // We should store the current incoming SDP settings, so that we can generate correct SDP in case of transport-replace
            // So if we receive sendonly SDP for the m=audio media, then we should not resend audio in case of reconnection
            if (this.peerjid.indexOf("mp_rvcp") !== -1) {
                if (this.localTransceivers[0].currentDirection === "recvonly") {
                    this.localTransceivers[0].direction = "recvonly";
                }
            }

            const sdp = await this.peerconnection.createOffer(options);
            this.contentUpdateOffer(sdp, mediaStreamType, "modify");
        }
        catch (error) {
            this.logger.log(`[Strophe.jingle] contentModify for session ${this.sid} failed ${error}`);
        }
    }

    public async contentUpdateOffer(sdp, mediaStreamType: string, actionType, addLocalType = false) {
        try {
            sdp = this.hackMsLabelSDP(sdp);
            if ((!this.conference_id || this.conference_id === -1) && (window as any).adapter.browserDetails.browser === "firefox") {
                sdp.sdp = this.removeSsrcGroupFidForFirefox(sdp.sdp);
            }

            this.logger.log(`[Strophe.jingle] contentUpdateOffer for session ${this.sid}:  ${sdp.sdp}`);

            this.localSDP = new SDP(sdp.sdp);

            if (mediaStreamType === "audio") { this.localType = "audio"; }
            else if (mediaStreamType === "video") { this.localType = "audio+video"; }
            else if (mediaStreamType === "sharing") { this.localType = "sharing"; }
            else if (mediaStreamType === "sharing+video") { this.localType = "sharing+video"; }

            let init;
            if (addLocalType) {
                init = $iq({ to: this.peerjid, type: 'set' })
                    .c('jingle', {
                        xmlns: 'urn:xmpp:jingle:1',
                        action: 'content-' + actionType,
                        initiator: this.initiator,
                        sid: this.sid,
                        mediaType: mediaStreamType,
                        localType: this.localType
                    });
            }
            else {
                init = $iq({
                    to: this.peerjid,
                    type: 'set'
                })
                    .c('jingle', {
                        xmlns: 'urn:xmpp:jingle:1',
                        action: 'content-' + actionType,
                        initiator: this.initiator,
                        sid: this.sid,
                        mediaType: mediaStreamType
                    });
            }

            if (this.calledNumber && this.calledNumber !== "") { //callednumber for webrtcGateway-mediaPillar
                init = init.c('mediapillar', { xmlns: 'urn:xmpp:janus:1' })
                    .c('callednumber')
                    .t(this.calledNumber);
                init.up();
                init.up();
            }

            this.localSDP.toJingle(init, this.initiator === this.me ? 'initiator' : 'responder', this.conference_id, this.bandwidthLimitSettings);
            this.connection.sendIQ(init,
                () => {
                    const ack: any = {};
                    ack.source = 'offer';
                },
                (stanza) => {
                    this.state = 'error';
                    this.logger.log(`[Strophe.jingle] IQ error: ${init}`);
                    if (this.peerconnection) {
                        this.logger.log('[Strophe.jingle] Clear peerconnection');
                        this.peerconnection.close();
                        this.peerconnection = null;
                    }

                    const error: any = ($(stanza).find('error').length)
                        ? {
                            code: $(stanza).find('error').attr('code'),
                            reason: $(stanza).find('error :first')[0].tagName,
                        }
                        : {};
                    error.source = 'offer';
                    this.connection.jingle.rxSubject.next({ name: 'jingleError', data: { sid: this.sid, error: error } });
                },
                this.jingleIqTimeout);

            sdp.sdp = this.localSDP.raw;
            await this.peerconnection.setLocalDescription(sdp);
            this.connection.jingle.rxSubject.next({ name: 'setLocalDescription.jingle', data: { sid: this.sid } });
            this.logger.log(`[Strophe.jingle] contentUpdateOffer -- setLocalDescription success for ${this.sid}`);
        }
        catch (error) {
            this.logger.log(`[Strophe.jingle] JingleSession.prototype.contentUpdateOffer failed:  ${error} ${error?.message}`);
        }
    }

    public async contentChangeAnswer(sdp, updateStreams) {
        try {
            this.logger.log(`[Strophe.jingle] contentChangeAnswer for session ${this.sid}:  ${sdp.sdp}`);

            sdp = this.hackMsLabelSDP(sdp);
            if ((!this.conference_id || this.conference_id === -1) && (window as any).adapter.browserDetails.browser === "firefox") {
                sdp.sdp = this.removeSsrcGroupFidForFirefox(sdp.sdp);
            }
            this.localSDP = new SDP(sdp.sdp);

            let init = $iq({
                to: this.peerjid,
                type: 'set'
            })
                .c('jingle', {
                    xmlns: 'urn:xmpp:jingle:1',
                    action: 'content-accept',
                    initiator: this.initiator,
                    sid: this.sid
                });

            if (this.calledNumber && this.calledNumber !== "") { //callednumber for webrtcGateway-mediaPillar
                init = init.c('mediapillar', { xmlns: 'urn:xmpp:janus:1' })
                    .c('callednumber')
                    .t(this.calledNumber);
                init.up();
                init.up();
            }

            this.localSDP.toJingle(init, this.initiator === this.me ? 'initiator' : 'responder', this.conference_id, this.bandwidthLimitSettings);
            this.connection.sendIQ(init,
                () => {
                    const ack: any = {};
                    ack.source = 'offer';
                },
                (stanza) => {
                    this.logger.log(`[Strophe.jingle] IQ error: ${init}`);
                    this.state = 'error';
                    if (this.peerconnection) {
                        this.logger.log('[Strophe.jingle] Clear peerconnection');
                        this.peerconnection.close();
                        this.peerconnection = null;
                    }
                    const error: any = ($(stanza).find('error').length)
                        ? {
                            code: $(stanza).find('error').attr('code'),
                            reason: $(stanza).find('error :first')[0].tagName,
                        }
                        : {};
                    error.source = 'offer';
                    this.connection.jingle.rxSubject.next({ name: 'jingleError', data: { sid: this.sid, error: error } });
                },
                this.jingleIqTimeout);

            sdp.sdp = this.localSDP.raw;
            await this.peerconnection.setLocalDescription(sdp);

            this.connection.jingle.rxSubject.next({ name: 'contentChangeAnswer.jingle', data: { sid: this.sid } });
            this.logger.log(`[Strophe.jingle] contentChangeAnswer -- setLocalDescription success for ${this.sid}`);
            if (updateStreams) {
                if (this.ice_config.citrix) {
                    //we need to update the transceivers
                    this.localTransceivers = await this.peerconnection["getTransceiversAsync"]();
                }
                this.updateRemoteStreams();
            }

            this.connection.jingle.rxSubject.next({ name: 'jingleMediaModified', data: { sid: this.sid } });
        } catch (error) {
            this.logger.log(`[Strophe.jingle] JingleSession.prototype.contentUpdateOffer failed:  ${error} ${error?.message}`);
        }
    }

    private setCodecPreferencesForSession() {
        if (this.localTransceivers[0] && this.ice_config.forceRedCodec) {
            this.setCodecPreference("audio", "red", this.localTransceivers[0]);
        }
        if (this.localTransceivers[1] && (this.company_config?.videoExt?.preferredCodec === "h264" || navigator.platform.toUpperCase().indexOf("MAC") >= 0)) {
            this.setCodecPreference("video", "h264", this.localTransceivers[1]);
        }
        if (this.localTransceivers[2] && (this.company_config?.sharingExt?.preferredCodec === "h264" || navigator.platform.toUpperCase().indexOf("MAC") >= 0)) {
            this.setCodecPreference("video", "h264", this.localTransceivers[2]);
        }
    }

    private sortByMimeTypes(codecs: RTCRtpCodec[], preferredOrder, preferredProfile?: string): RTCRtpCodec[] {
        codecs = codecs.sort((codecA, codecB) => {
            const indexA = preferredOrder.indexOf(codecA.mimeType);
            const indexB = preferredOrder.indexOf(codecB.mimeType);
            const orderA = indexA >= 0 ? indexA : Number.MAX_VALUE;
            const orderB = indexB >= 0 ? indexB : Number.MAX_VALUE;
            return orderA - orderB;
        });

        if (preferredProfile) {
            codecs = this.setPreferredProfileForCodec(codecs, preferredOrder[0], preferredProfile);
        }

        return codecs;
    }

    private setPreferredProfileForCodec(codecs: RTCRtpCodec[], codec, preferredProfile): RTCRtpCodec[] {
        return codecs.sort((codecA, codecB) => {
            const indexA = (codecA.mimeType === codec && codecA?.sdpFmtpLine) ? codecA.sdpFmtpLine.indexOf(preferredProfile) : -1;
            const indexB = (codecB.mimeType === codec && codecB?.sdpFmtpLine) ? codecB.sdpFmtpLine.indexOf(preferredProfile) : -1;
            const orderA = indexA >= 0 ? indexA : Number.MAX_VALUE;
            const orderB = indexB >= 0 ? indexB : Number.MAX_VALUE;
            return orderA - orderB;
        });
    }

    //supported codecs are
    private setCodecPreference(media: string, preferredCodec: string, transceiver: RTCRtpTransceiver) {
        if (!RTCRtpReceiver?.getCapabilities || !transceiver.setCodecPreferences) {
            this.logger.log(`[Strophe.jingle] setCodecPreference ${this.sid} not supported`);
            return;
        }

        try {
            this.logger.log(`[Strophe.jingle] set codec preference for ${media} ${this.sid} to ${preferredCodec}`);
            const supportedCodecs = RTCRtpReceiver.getCapabilities(media).codecs;
            let preferredCodecs;
            let preferredProfile;
            if (preferredCodec === 'h264') { preferredCodecs = ["video/H264", "video/VP8", "video/VP9"]; preferredProfile = "42e01f"; }
            else if (preferredCodec === 'red') { preferredCodecs = ["audio/red", "audio/opus"]; }
            const sortedCodecs = this.sortByMimeTypes(supportedCodecs, preferredCodecs, preferredProfile);
            transceiver.setCodecPreferences(sortedCodecs);
        }
        catch (error) {
            this.logger.log(`[Strophe.jingle] set codec preference for ${media} ${this.sid} to ${preferredCodec} error -- ${error}`);
        }
    }

    public async getSenders(): Promise<RTCRtpSender[]> {
        if (this.ice_config.citrix && this.peerconnection["getSendersAsync"]) {
            await this.peerconnection["getSendersAsync"]();
        }
        return this.peerconnection.getSenders();
    }

    public async setRemoteDescription(elem, desctype) {
        this.remoteSDP = new SDP('');
        this.remoteSDP.fromJingle(elem, this);

        if (!localStorage.getItem('disablePassive')) {
            if (this.isInitiator) {
                this.remoteSDP.raw = this.remoteSDP.raw.replace(/a=setup:passive/g, "a=setup:active");
            } else {
                this.remoteSDP.raw = this.remoteSDP.raw.replace(/a=setup:active/g, "a=setup:passive");
            }
        }

        //activate Discontinuous Transmission (DTX)
        if (this.DTX) {
            this.logger.log('[Strophe.jingle] activate Discontinuous Transmission (DTX)');
            this.remoteSDP.raw = this.remoteSDP.raw.replace("useinbandfec=1", "useinbandfec=1;usedtx=1");
        }

        this.logger.log(`[Strophe.jingle] setRemoteDescription ${desctype} after parsing for session ${this.sid} --  ${this.remoteSDP.raw}`);
        const remotedesc = new window.RTCSessionDescription({ type: desctype, sdp: this.remoteSDP.raw });
        try {
            await this.peerconnection.setRemoteDescription(remotedesc);
            this.logger.log('[Strophe.jingle] setRemoteDescription success');
            this.connection.jingle.rxSubject.next({ name: 'setRemoteDescription.jingle', data: { sid: this.sid } });
            this.localTransceivers = this.peerconnection.getTransceivers();

            //remove the wrong receivers for horizon
            if (this.ice_config.horizon) {
                this.localTransceivers.forEach((transceiver, index) => {
                    if (!transceiver["id"] && !transceiver.mid) {
                        this.localTransceivers.splice(index, 1);
                    }
                });

                //also order
                this.localTransceivers = this.localTransceivers.sort((aa, bb) => {
                    return (parseInt(aa.mid, 10) < parseInt(bb.mid, 10)) ? -1 : 1;
                });
            }

            //set codec preferences
            if ((!this.conference_id || this.conference_id === -1)) {
                //set codec preferences based on the configuration
                //only for direct P2P calls and no conferences
                this.setCodecPreferencesForSession();
            }

            //once the remote description is set, we need to send event to update the call
        }
        catch (error) {
            const msg = (error?.toString) ? error.toString() : error;
            this.logger.error(`[Strophe.jingle] setRemoteDescription for ${this.sid} failed -- ${msg}`);
            this.logger.error(error);
        }
    }

    public addIceCandidate(elem) {

        // operate on each content element
        elem.each((__index, el) => {

            ////// to modify
            let idx = -1;
            let i;
            for (i = 0; i < this.remoteSDP.media.length; i++) {
                if (SDPUtil.find_line(this.remoteSDP.media[i], 'a=mid:' + $(el).attr('name'))
                    || this.remoteSDP.media[i].indexOf('m=' + $(el).attr('name')) === 0) {
                    idx = i;
                    break;
                }
            }
            if (idx === -1) { // fall back to localdescription
                for (i = 0; i < this.localSDP.media.length; i++) {
                    if (SDPUtil.find_line(this.localSDP.media[i], 'a=mid:' + $(el).attr('name'))
                        || this.localSDP.media[i].indexOf('m=' + $(el).attr('name')) === 0) {
                        idx = i;
                        break;
                    }
                }
            }

            ////// to modify end
            const name = $(el).attr('name');

            let ufrag;
            let pwd;

            // const network = "";
            // const id = "";
            // network = $(this).attr('network');
            // id = $(this).attr('id');

            $(el).find('content>transport').each(function () {
                ufrag = this.getAttribute("ufrag");
                pwd = this.getAttribute("pwd");
            });

            $(el).find('transport>candidate').each((__index, cand) => {
                const line = SDPUtil.candidateFromJingle(cand, ufrag, pwd);
                const candidate = new window.RTCIceCandidate({
                    sdpMLineIndex: idx,
                    sdpMid: name,
                    candidate: line
                });
                try {
                    if (candidate && this.iceFilterList && this.iceFilterList.length) {
                        if (this.iceFilterList.indexOf(candidate.type) !== -1) {
                            this.logger.log(`[Strophe.jingle] ICE candidate ${candidate.type} for ${this.sid} is filtered`);
                            return;
                        }
                    }
                    this.logger.log(`[Strophe.jingle] Add ICE candidate ${candidate.type} for ${this.sid}`);

                    //in citrix, it's not a promise...
                    if (this.ice_config.citrix || this.ice_config.horizon) {
                        this.peerconnection.addIceCandidate(candidate);
                    }
                    else {
                        this.peerconnection.addIceCandidate(candidate)
                            .then(() => {
                            // do nothing
                            })
                            .catch((error) => { this.logger.log(`[Strophe.jingle] Add ICE candidate ${candidate.type} for ${this?.sid} failure -- ${error?.message}`); });
                    }

                } catch (err) {
                    this.logger.error('[Strophe.jingle] addIceCandidate failed ' + err.toString());
                }
            });
        });
    }

    //only 2 values accepted, session-accept and transport-replace in case of reconnection (transport-replace offer)
    public async sendAnswer(type: string = "session-accept"): Promise<void> {
        try {
            this.logger.log(`[Strophe.jingle] sendAnswer ${type} for session ${this.sid}`);
            const sdp = await this.peerconnection.createAnswer(this.media_constraints);
            this.state = 'active';
            this.createdAnswer(sdp, type);
        }
        catch (error) {
            this.logger.error(`[Strophe.jingle] createAnswer failed for session ${this.sid} -- ${error}`);
        }
    }

    private async createdAnswer(sdp: RTCSessionDescriptionInit, type: string) {
        try {
            sdp = this.hackMsLabelSDP(sdp);
            if ((!this.conference_id || this.conference_id === -1) && (window as any).adapter.browserDetails.browser === "firefox") {
                sdp.sdp = this.removeSsrcGroupFidForFirefox(sdp.sdp);
            }

            this.localSDP = new SDP(sdp.sdp);

            const action = type ? type : "session-accept";

            if (type === "transport-accept") this.isReconnecting = false;

            this.logger.log(`[Strophe.jingle] createdAnswer for session ${this.sid}:  ${sdp.sdp}`);

            let accept = $iq({
                to: this.peerjid,
                type: 'set'
            })
                .c('jingle', {
                    xmlns: 'urn:xmpp:jingle:1',
                    action: action,
                    initiator: this.initiator,
                    responder: this.responder,
                    sid: this.sid,
                    localType: this.localType
                });

            if (this.calledNumber && this.calledNumber !== "") { //callednumber for webrtcGateway-mediaPillar
                accept = accept.c('mediapillar', { xmlns: 'urn:xmpp:janus:1' })
                    .c('callednumber')
                    .t(this.calledNumber);
                accept.up();
                accept.up();
            }

            this.localSDP.toJingle(accept, this.initiator === this.me ? 'initiator' : 'responder', this.conference_id, this.bandwidthLimitSettings);
            // this.addConferenceInfo(accept);
            this.connection.sendIQ(accept,
                () => {
                    const ack: any = {};
                    ack.source = 'answer';
                },
                (stanza) => {
                    const error: any = ($(stanza).find('error').length)
                        ? {
                            code: $(stanza).find('error').attr('code'),
                            reason: $(stanza).find('error :first')[0].tagName,
                        }
                        : {};
                    error.source = 'answer';
                    this.connection.jingle.rxSubject.next({ name: 'jingleError', data: { sid: this.sid, error: error } });

                },
                this.jingleIqTimeout);

            sdp.sdp = this.localSDP.raw;
            await this.peerconnection.setLocalDescription(sdp);
            this.logger.log(`[Strophe.jingle] createdAnswer -- setLocalDescription success for ${this.sid}`);
            this.connection.jingle.rxSubject.next({ name: 'setLocalDescription.jingle', data: { sid: this.sid } });
        } catch (error) {
            this.logger.log(`[Strophe.jingle] JingleSession.prototype.createdAnswer failed: ${error} ${error?.message}`);
        }
    }

    public sendTerminate(reason: string | null, text: string = "") {
        try {
            const term = $iq({ to: this.peerjid, type: 'set' })
                .c('jingle', { xmlns: 'urn:xmpp:jingle:1', action: 'session-terminate', initiator: this.initiator, sid: this.sid })
                .c('reason')
                .c(reason || 'success');

            if (text) term.up().c('text').t(text);

            this.connection.sendIQ(term,
                () => {
                    if (this.peerconnection && (this.peerconnection.signalingState !== 'closed')) {
                        this.peerconnection.close();
                        this.terminate();
                    }
                    this.logger.log(`[Strophe.jingle] Clear peerconnection for ${this?.sid}`);
                    this.peerconnection = null;
                    const ack: any = {};
                    ack.source = 'terminate';
                },
                (stanza) => {
                    const error: any = ($(stanza).find('error').length)
                        ? {
                            code: $(stanza).find('error').attr('code'),
                            reason: $(stanza).find('error :first')[0].tagName,
                        }
                        : {};
                    this.connection.jingle.rxSubject.next({ name: 'add.jingle', data: { sid: this.sid, error: error } });
                },
                this.jingleIqTimeout);
            if (this.statsinterval !== null) {
                window.clearInterval(this.statsinterval);
                this.statsinterval = null;
            }
        } catch (error) {
            this.logger.log(`[Strophe.jingle] JingleSession.prototype.sendTerminate failed for ${this?.sid} with error: ${error}`);
        }
    }

    public mediaModified(updateStreams?: any) {
        this.peerconnection.createAnswer(this.media_constraints)
            .then((sdp) => { this.contentChangeAnswer(sdp, updateStreams); })
            .catch((error) => {
                this.logger.error('[Strophe.jingle] mediaModified failed: ' + error);
                this.logger.log(error);
            });
    }

    public muteAudio(muted) {
        if (this.connection.jingle.localStream) {
            this.connection.jingle.localStream.getAudioTracks().forEach(function (track) {
                track.enabled = !muted;
            });
        }

        if (this.localStreams.length >= 1) {
            this.localStreams.forEach(function (stream) {
                //do not mute the system audio stream in case of sharing + system audio
                if (!stream["sharingAndAudio"]) {
                    stream.getAudioTracks().forEach(function (track) {
                        track.enabled = !muted;
                    });
                }
            });
        }
    }

    public sendMute(muted, content) {
        try {
            const info = $iq({
                to: this.peerjid,
                type: 'set'
            })
                .c('jingle', {
                    xmlns: 'urn:xmpp:jingle:1',
                    action: 'session-info',
                    initiator: this.initiator,
                    sid: this.sid
                });
            info.c(muted ? 'mute' : 'unmute', { xmlns: 'urn:xmpp:jingle:apps:rtp:info:1' });
            info.attrs({ creator: this.me === this.initiator ? 'creator' : 'responder' });
            if (content) {
                info.attrs({ name: content });
            }
            this.connection.sendIQ(info);
        } catch (error) {
            this.logger.error('[Strophe.jingle] JingleSession.prototype.sendMute failed: ' + error);
        }
    }

    public sendRefer(confendpointId, referTo) {
        try {
            const info = $iq({
                to: this.peerjid,
                type: 'set'
            })
                .c('jingle', {
                    xmlns: 'urn:xmpp:jingle:1',
                    action: 'session-info',
                    initiator: this.initiator,
                    sid: this.sid
                });
            info.c('refer', { xmlns: 'urn:xmpp:jingle:apps:rtp:info:1' });
            info.attrs({ confEndpointId: confendpointId, referTo: referTo });
            this.connection.sendIQ(info);
        } catch (error) {
            this.logger.error('[Strophe.jingle] JingleSession.prototype.sendRefer failed: ' + error);
        }
    }

    public sendP2PTransferCallRefer(replaceSid, referTo) {
        try {
            const info = $iq({
                to: this.peerjid,
                type: 'set'
            })
                .c('jingle', {
                    xmlns: 'urn:xmpp:jingle:1',
                    action: 'session-info',
                    initiator: this.initiator,
                    sid: this.sid
                });
            info.c('refer', { xmlns: 'urn:xmpp:jingle:apps:rtp:info:1' });
            info.attrs({ sid: replaceSid, referTo: referTo });
            this.connection.sendIQ(info);
        } catch (error) {
            this.logger.error('[Strophe.jingle] JingleSession.prototype.sendP2PTransferCallRefer failed: ' + error);
        }
    }

    public sendReferStatus(status) {
        try {
            const info = $iq({
                to: this.peerjid,
                type: 'set'
            })
                .c('jingle', {
                    xmlns: 'urn:xmpp:jingle:1',
                    action: 'session-info',
                    initiator: this.initiator,
                    sid: this.sid
                });
            info.c('refer', { xmlns: 'urn:xmpp:jingle:apps:rtp:info:1' });
            info.attrs({ status: status });
            this.connection.sendIQ(info);
        } catch (error) {
            this.logger.error('[Strophe.jingle] JingleSession.prototype.sendReferStatus failed: ' + error);
        }
    }

    public sendReferTransfer(method, referTo) { //RQRAINB-5819
        try {
            const info = $iq({
                to: this.peerjid,
                type: 'set'
            })
                .c('jingle', {
                    xmlns: 'urn:xmpp:jingle:1',
                    action: 'session-info',
                    initiator: this.initiator,
                    sid: this.sid
                });
            info.c('refer', { xmlns: 'urn:xmpp:jingle:apps:rtp:info:1' });
            info.attrs({ method: method, referTo: referTo });
            this.connection.sendIQ(info);
        } catch (error) {
            this.logger.error('[Strophe.jingle] JingleSession.prototype.sendRefertransfer failed: ' + error);
        }
    }

    public sendHold(hold: boolean): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.onHold === hold) resolve();
            else {
                // Create the info stanza
                const sessionInfo = $iq({ to: this.peerjid, type: 'set' })
                    .c('jingle', {
                        xmlns: 'urn:xmpp:jingle:1',
                        action: 'session-info',
                        initiator: this.initiator,
                        sid: this.sid
                    })
                    .c(hold ? 'hold' : 'unhold', { xmlns: 'urn:xmpp:jingle:apps:rtp:info:1' });

                this.connection.sendIQ(
                    sessionInfo,
                    () => { this.onHold = hold; resolve(); },
                    (error: any) => {
                        this.logger.error('[Strophe.jingle] JingleSession.prototype.sendHold failed: ' + error?.message);
                        reject(error);
                    }
                );
            }
        });
    }

    /*public sendHold(hold) {
        try {
            if (this.onHold === hold) return;
            const info = $iq({
                to: this.peerjid,
                type: 'set'
            })
                .c('jingle', {
                    xmlns: 'urn:xmpp:jingle:1',
                    action: 'session-info',
                    initiator: this.initiator,
                    sid: this.sid
                });
            info.c(hold ? 'hold' : 'unhold', { xmlns: 'urn:xmpp:jingle:apps:rtp:info:1' });
            this.connection.sendIQ(info);
            this.onHold = hold;
        } catch (error) {
            this.logger.error('[Strophe.jingle] JingleSession.prototype.sendMute failed: ' + error);
        }
    }*/

    public sendIceFailed() {
        try {
            const info = $iq({
                to: this.peerjid,
                type: 'set'
            })
                .c('jingle', {
                    xmlns: 'urn:xmpp:jingle:1',
                    action: 'session-info',
                    initiator: this.initiator,
                    sid: this.sid
                });
            info.c('iceFailed', { xmlns: 'urn:xmpp:jingle:apps:rtp:info:1' });

            this.connection.sendIQ(info);
        } catch (error) {
            this.logger.error('[Strophe.jingle] JingleSession.prototype.sendIceFailed failed: ' + error);
        }
    }

    public sendRinging() {
        try {
            const info = $iq({
                to: this.peerjid,
                type: 'set'
            })
                .c('jingle', {
                    xmlns: 'urn:xmpp:jingle:1',
                    action: 'session-info',
                    initiator: this.initiator,
                    sid: this.sid
                });
            info.c('ringing', { xmlns: 'urn:xmpp:jingle:apps:rtp:info:1' });

            this.connection.sendIQ(info);
        } catch (error) {
            this.logger.error('[Strophe.jingle] JingleSession.prototype.sendRinging failed: ' + error);
        }
    }

    public modifyEncodingState(state, customMuteAction) {
        this.logger.log('[Strophe.jingle] modifyEncodingState ' + state);
        const videoSender = this.peerconnection.getSenders().find(sender => sender.track.kind === 'video');
        const parameters = videoSender.getParameters();
        let index = 2;
        //desktop app currently has issue with the RID parameter
        if (parameters.encodings[0].rid && parameters.encodings[0].rid === "h") {
            index = 0;
        }
        parameters.encodings[index].active = state;

        if (customMuteAction) {
            parameters.encodings[0].active = state;
            parameters.encodings[2].active = state;
        }

        videoSender.setParameters(parameters);
    }

    public enableHQEncoding(enableHQEncoding) {
        return (async (enable) => {
            try {
                this.logger.log('[Strophe.jingle.session] enableHQEncoding ' + enable);
                const videoSender = this.peerconnection.getSenders()[1];
                if (videoSender) {
                    const parameters = videoSender.getParameters();
                    parameters.encodings.forEach((encoding) => { if (encoding.rid !== 'l') encoding.active = enable; });
                    await videoSender.setParameters(parameters);
                }
            }
            catch (error) {
                this.logger.error('[Strophe.jingle.session] enableHQEncoding(' + enable + ') -- failure -- ' + error.message);
            }
        })(enableHQEncoding);
    }

    //API for test/debug purposes only
    public disableSubStreamLevel(levelIndex) {
        const videoSender = this.peerconnection.getSenders().find(sender => sender.track.kind === 'video');
        const parameters = videoSender.getParameters();

        //desktop app currently has issue with the RID parameter
        if (levelIndex === 2 && parameters.encodings[0].rid && parameters.encodings[0].rid === "h") {
            levelIndex = 0;
        }
        else if (levelIndex === 0 && parameters.encodings[2].rid && parameters.encodings[2].rid === "l") {
            levelIndex = 2;
        }

        this.logger.log('[Strophe.jingle] disableSubStreamLevel ' + levelIndex + ' state: ' + !(parameters.encodings[levelIndex].active));
        this.logger.log(parameters.encodings[levelIndex].rid);

        parameters.encodings[levelIndex].active = !(parameters.encodings[levelIndex].active);

        videoSender.setParameters(parameters);
    }

    //no longer used, but the code is nice to have just in case
    // public forceCodecTo(sdp, codecToForce, media) {
    //     try {
    //         const splittedSDP = sdp.split('\r\n');
    //         let indexVideo = -1;
    //         let indexPos = -1;
    //         const codec: any = [];
    //         let list = [];
    //         let from; let to; let codecName; let indexFound; let j;

    //         for (let i = 0; i < splittedSDP.length; i++) {

    //             const line = splittedSDP[i];

    //             if (line.indexOf('m=' + media) > -1) {
    //                 indexVideo = i;
    //                 indexPos = line.indexOf('RTP/SAVPF') + 10;
    //                 list = line.substring(indexPos).split(" ");
    //             }

    //             if (line.indexOf(codecToForce) > -1) {
    //                 from = line.indexOf(':');
    //                 to = line.indexOf(' ');
    //                 codecName = line.substring(from + 1, to);
    //                 indexFound = -1;
    //                 codec.push(codecName);
    //                 //Remove from list
    //                 for (j = 0; j < list.length; j++) {
    //                     if (list[j] === codecName) {
    //                         indexFound = j;
    //                         break;
    //                     }
    //                 }
    //                 if (indexFound > -1) {
    //                     list.splice(indexFound, 1);
    //                 }
    //             }

    //             if (codecToForce === 'PCMU/8000') {
    //                 // Do it again with the second codec
    //                 if (line.indexOf('PCMA/8000') > -1) {
    //                     from = line.indexOf(':');
    //                     to = line.indexOf(' ');
    //                     codecName = line.substring(from + 1, to);
    //                     indexFound = -1;
    //                     codec.push(codecName);
    //                     //Remove from list
    //                     for (j = 0; j < list.length; j++) {
    //                         if (list[j] === codecName) {
    //                             indexFound = j;
    //                             break;
    //                         }
    //                     }
    //                     if (indexFound > -1) {
    //                         list.splice(indexFound, 1);
    //                     }
    //                 }
    //             }
    //         }

    //         if (indexVideo !== -1) {
    //             splittedSDP[indexVideo] = splittedSDP[indexVideo].substr(0, indexPos - 1);
    //             // Add  requested codec first
    //             for (let ind = 0; ind < codec.length; ind++) {
    //                 splittedSDP[indexVideo] += " " + codec[ind];
    //             }

    //             if (list.length > 0) {
    //                 splittedSDP[indexVideo] += " " + list.join(' ');
    //             }

    //             sdp = splittedSDP.join('\r\n');
    //         }

    //         return sdp;
    //     }
    //     catch {
    //         return sdp;
    //     }
    // }

    // Remove ssrc-group:FID and second ssrc (firefox issue, seems not to work anyway..)
    public removeSsrcGroupFidForFirefox(sdp: string) {
        this.logger.log("[Strophe.jingle] session -- removeSsrcGroupFidForFirefox");
        const lines = sdp.split("\r\n");
        let mediaLine = false;
        for (let i = 0; i < lines.length; i++) {
            //find m=video before starting calculing
            //then find video if sharing + video
            if (lines[i] && lines[i].indexOf("m=video") !== -1) {
                mediaLine = true;
            }

            //remove second ssrc from SDP
            if (mediaLine && lines[i].indexOf("a=ssrc:") !== -1) {
                //check if next line is also SSRC
                const j = i + 1;
                if (lines[j] && lines[j].indexOf("a=ssrc:") !== -1) {
                    lines.splice(j, 1);
                }
            }

            //remove ssrc-group:FID from SDP
            if (mediaLine && lines[i] && lines[i].indexOf("a=ssrc-group:FID") !== -1) {
                lines.splice(i, 1);
            }
        }

        sdp = lines.join("\r\n");
        if (!sdp.endsWith("\r\n"))
            sdp += "\r\n";
        return sdp;
    }

    public async setDSCPValues() {
        try {
            if (this.enableDSCP) {

                //conf and webinar
                if (this.conference_id && this.conference_id !== -1) {

                    if (this.localType === "sharing") {
                        const params = this.localTransceivers[0].sender.getParameters();
                        if (!params.encodings[0]) { params.encodings[0] = {}; }
                        params.encodings[0].networkPriority = "medium";
                        await this.localTransceivers[0].sender.setParameters(params);
                    }

                    if (this.localType === "video") {
                        const params = this.localTransceivers[0].sender.getParameters();
                        if (!params.encodings[0]) { params.encodings[0] = {}; }
                        params.encodings[0].networkPriority = "medium";
                        await this.localTransceivers[0].sender.setParameters(params);
                    }

                    //manage webinar cases (bundle audio+video)
                    if (this.localType === "audio+video") {
                        if (this.localTransceivers[0]?.sender) {
                            const params = this.localTransceivers[0].sender.getParameters();
                            if (params.encodings[0]) {
                                params.encodings[0].priority = "high";
                                params.encodings[0].networkPriority = "high";
                                await this.localTransceivers[0].sender.setParameters(params);
                            }
                        }

                        //video
                        if (this.localTransceivers[1]?.sender) {
                            const paramsVideo = this.localTransceivers[1].sender.getParameters();
                            if (paramsVideo.encodings[0]) {
                                paramsVideo.encodings[0].priority = "medium";
                                paramsVideo.encodings[0].networkPriority = "high";
                                await this.localTransceivers[1].sender.setParameters(paramsVideo);
                            }
                        }
                    }
                }
                else {
                    //manage DSCP for P2P sessions
                    //audio
                    if (this.localTransceivers[0]?.sender) {
                        const params = this.localTransceivers[0].sender.getParameters();
                        if (params.encodings[0]) {
                            params.encodings[0].priority = "high";
                            params.encodings[0].networkPriority = "high";
                            await this.localTransceivers[0].sender.setParameters(params);
                        }
                    }

                    //video
                    if (this.localTransceivers[1]?.sender) {
                        const paramsVideo = this.localTransceivers[1].sender.getParameters();
                        if (paramsVideo.encodings[0]) {
                            paramsVideo.encodings[0].priority = "medium";
                            paramsVideo.encodings[0].networkPriority = "high";
                            await this.localTransceivers[1].sender.setParameters(paramsVideo);
                        }
                    }

                    //sharing
                    if (this.localTransceivers[2]?.sender) {
                        const paramsSharing = this.localTransceivers[2].sender.getParameters();
                        if (paramsSharing.encodings[0]) {
                            paramsSharing.encodings[0].priority = "low";
                            paramsSharing.encodings[0].networkPriority = "high";
                            await this.localTransceivers[2].sender.setParameters(paramsSharing);
                        }
                    }
                }
            }

            //update conference priority
            if (this.conference_id && this.conference_id !== -1) {
                //check for local flags
                let networkPriority;
                if (this.localType === "video") { networkPriority = localStorage.getItem('networkPriorityVideo'); }
                if (this.localType === "sharing") { networkPriority = localStorage.getItem('networkPrioritySharing'); }
                if (this.localType === "audio") { networkPriority = localStorage.getItem('networkPriority'); }

                if (networkPriority) {
                    const params = this.localTransceivers[0].sender.getParameters();
                    if (!params.encodings[0]) { params.encodings[0] = {}; }
                    params.encodings[0].networkPriority = networkPriority;
                    await this.localTransceivers[0].sender.setParameters(params);
                }

                if (this.localType === "audio") {
                    const params = this.localTransceivers[0].sender.getParameters();
                    if (!params.encodings[0]) { params.encodings[0] = {}; }
                    if (params.encodings[0].priority && params.encodings[0].priority !== "high") {
                        params.encodings[0].priority = "high";
                        await this.localTransceivers[0].sender.setParameters(params);
                    }
                }
            }
        }
        catch (error) {
            //do nothing
            this.logger.warn(`[Strophe.jingle] Enabling QoS DSCP for session ${this.sid} -- error ${error}`);
        }
    }

    public webinarGetMedia(): any {
        let audio = false;
        let video = false;
        this.remoteSDP?.media.forEach((media) => {
            if (media.startsWith('m=audio')) audio = true;
            if (media.startsWith('m=video') && !media.includes('a=inactive')) video = true;
        });
        return { audio, video };

        /*
        const stream: MediaStream = this.remoteStreamsObject?.["0"] ? session.remoteStreamsObject["0"] : null;
        const videoTrack = stream.getVideoTracks()[0];
        if (!videoTrack || videoTrack.muted) return { audio: true, video: false };
        return { audio: true, video: true };
        */
    }
}
